import * as Yup from 'yup';
import type { DimensionItem } from 'src/components/star-grid/components/RowsDimensionPicker';
import type { DataSyncText } from 'src/components/star-grid/hooks/useDataSyncText';
import { CreateReportFormValues } from 'src/components/star-grid/createReport.types';
import { PnlConfigResponse, PnlHierarchyGroup } from 'src/components/star-grid/starGridTypes';
import { PNL_STATEMENT_LINE_DIMENSION } from 'src/components/star-grid/starGridConstants';

export const reportDimensionItems = (config: PnlConfigResponse | undefined, text: DataSyncText): DimensionItem[] => {
  const dimensions = new Map<string, DimensionItem>();
  dimensions.set(PNL_STATEMENT_LINE_DIMENSION, { field: PNL_STATEMENT_LINE_DIMENSION, label: text('createStatementLine') });
  for (const dimension of config?.available_dimensions ?? []) {
    const field = dimension.fact_key?.trim();
    if (field && !dimensions.has(field)) dimensions.set(field, { field, label: dimension.label || field });
  }
  return [...dimensions.values()];
};

/**
 * Draggable dimension chips sourced from the ROLLUP HIERARCHIES (dimension_registry.json ∩
 * pnl_paths, via GET /pnl-hierarchies) rather than the flat available_dimensions list. One chip
 * per hierarchy, so a dimension offering several appears once per rollup (line item → "P&L Line
 * Item" and "Channel"); each chip carries the hierarchyId its value picker reads.
 */
export const hierarchyDimensionItems = (groups: readonly PnlHierarchyGroup[] | undefined): DimensionItem[] =>
  (groups ?? []).flatMap((group) =>
    group.hierarchies.map((hierarchy) => ({
      field: hierarchy.fact_key,
      label: hierarchy.label,
      hierarchyId: hierarchy.hierarchy_id
    }))
  );

export const initialCreateReportValues = (_text: DataSyncText): CreateReportFormValues => ({
  report_name: '',
  report_description: '',
  // Seeded once the hierarchies load (see useCreatePnlReportForm) — the registry is async.
  selected_dimensions: [],
  selected_filters: [],
  scenario_selections: [],
  variance_pairs: [],
  row_values: {},
  filter_values: {}
});

const scenarioColumnSchema = (text: DataSyncText) =>
  Yup.object({
    scenario: Yup.object({
      reporting_scenario_id: Yup.number()
        .integer(text('createInvalidScenario'))
        .typeError(text('createInvalidScenario'))
        .required(text('createInvalidScenario')),
      scenario_name: Yup.string().trim().required(text('createInvalidScenario'))
    })
      .nullable()
      .required(text('createScenarioRequired')),
    selectedMonths: Yup.array()
      .of(Yup.number().integer(text('createInvalidMonths')).typeError(text('createInvalidMonths')).required(text('createInvalidMonths')))
      .min(1, text('createMonthsRequired'))
      .required(text('createMonthsRequired'))
  });

export const createReportFormSchema = (text: DataSyncText) =>
  Yup.object({
    report_name: Yup.string().trim().required(text('createNameRequired')),
    selected_dimensions: Yup.array()
      .of(Yup.object({ field: Yup.string().trim().required(text('createRowsRequired')) }))
      .min(1, text('createRowsRequired'))
      .required(text('createRowsRequired')),
    scenario_selections: Yup.array().of(scenarioColumnSchema(text)).min(1, text('createScenariosRequired')).required(text('createScenariosRequired'))
  });

export const reportFieldError = (error: unknown, show: boolean): string | undefined => {
  if (!show || !error) return undefined;
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return undefined;
  const messages = Object.values(error)
    .map((value) => reportFieldError(value, true))
    .filter(Boolean);
  return [...new Set(messages)].join(' ') || undefined;
};

export const reportColumnsError = (errors: unknown, show: boolean, text: DataSyncText): string | undefined => {
  if (!show || !Array.isArray(errors)) return reportFieldError(errors, show);
  return (
    errors
      .map((error, index) => {
        const message = reportFieldError(error, true);
        return message
          ? text('createColumnError')
              .replace('{column}', String(index + 1))
              .replace('{error}', message)
          : '';
      })
      .filter(Boolean)
      .join(' ') || undefined
  );
};
